import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import type { Agent } from "@/features/agents/types";
import { useToolActivation } from "@/features/tools/hooks/use-tool-activation";
import { useToolCatalog } from "@/features/tools/hooks/use-tool-catalog";
import {
  useAvailableTools,
  useCreateClientTool,
  useDeleteAgentConfiguration,
  useUpdateAgentConfiguration,
  useUpdateClientTool,
} from "@/features/tools/hooks/use-tools";
import { toolConfiguration } from "@/features/tools/hooks/use-tool-activation";
import type { AvailableTool } from "@/features/tools/types";
import { useTranslation } from "@/features/utils/i18n";
import { ToolActivationSwitch } from "@/views/backoffice/tools/components/tool-activation-switch";
import { ToolList } from "@/views/backoffice/tools/components/tool-list";
import _ from "lodash";
import { useEffect, useState } from "react";
import { toast } from "sonner";

/** Whether this agent uses the module: its own switch, else the company's. */
export const isOnForAgent = (item: AvailableTool, agentId: string) => {
  const override = item.clientTool?.per_agents?.[agentId];
  if (!item.clientTool) return false;
  return override?.enabled ?? item.clientTool.enabled ?? false;
};

/** Settings this agent holds apart from the company's. */
export const overriddenFields = (item: AvailableTool, agentId: string) =>
  Object.values(
    item.clientTool?.per_agents?.[agentId]?.configuration || {},
  ).filter((value) => value !== undefined).length;

/**
 * Whether this agent does anything differently from the company on this
 * module: a setting of its own, or a switch that disagrees with the company's.
 * An empty entry, or a switch saying what the company already says, is not one.
 */
export const hasAgentOverride = (item: AvailableTool, agentId: string) => {
  const override = item.clientTool?.per_agents?.[agentId];
  if (!override) return false;
  return (
    overriddenFields(item, agentId) > 0 ||
    (override.enabled !== undefined &&
      override.enabled !== item.clientTool?.enabled)
  );
};

/**
 * Whether this module is shown as the company's on this agent's page: only
 * for the company's sole agent, and only while it holds nothing of its own.
 *
 * With one agent there is nobody to differ from, so the screens offer no way
 * to make an override — but one can still be in the database (made when there
 * were several agents, or by an older screen), and it wins on every call. It
 * stays on screen until removed: a sole agent booking through another calendar
 * than the one the company form shows was exactly that, invisible.
 */
export const sharedForAgent = (
  item: AvailableTool,
  agentId: string,
  soleAgent: boolean,
) => soleAgent && !hasAgentOverride(item, agentId);

/**
 * What the agent can do, each module one line with its switch.
 *
 * With a single agent there is nothing to tell apart: the switch is the
 * company's, exactly the one on the modules page — unless that agent still
 * holds an override of the module (`sharedForAgent`). With several, it is this
 * agent's, and switching a module on or off asks whether it is for this agent
 * or for all of them — the defaults being good enough, that choice is most of
 * what anyone comes here to make.
 */
export function AgentSkills({
  agent,
  agents,
  onOpen,
}: {
  agent: Agent;
  /** Every agent of the company, this one included. */
  agents: Agent[];
  onOpen: (toolId: string) => void;
}) {
  const { t } = useTranslation("agents");
  const { data: available = [], isLoading } = useAvailableTools();
  const otherAgentIds = agents
    .filter((other) => other.id !== agent.id)
    .map((other) => other.id);
  const soleAgent = otherAgentIds.length === 0;

  return (
    <ToolList
      items={available}
      isLoading={isLoading}
      isOn={(item) => isOnForAgent(item, agent.id)}
      activeTitle={(count) => t("agents.skills.active", { count })}
      inactiveTitle={t("agents.skills.available")}
      canOpen={(_item, on) => on}
      onOpen={(item) => onOpen(item.tool.id)}
      renderBadges={(item, on) => {
        const shared = sharedForAgent(item, agent.id, soleAgent);
        const overrides = shared ? 0 : overriddenFields(item, agent.id);
        // Off for this agent while the others have it: worth saying, it is
        // the one case where the modules page and this one disagree.
        const offHereOnly = !shared && !on && !!item.clientTool?.enabled;
        return (
          <>
            {overrides > 0 && (
              <Badge variant="secondary" className="font-normal">
                {t("agents.skills.customized", { count: overrides })}
              </Badge>
            )}
            {offHereOnly && (
              <Badge variant="outline" className="font-normal">
                {t("agents.skills.offForThisAgent")}
              </Badge>
            )}
          </>
        );
      }}
      renderSwitch={(item, on) =>
        sharedForAgent(item, agent.id, soleAgent) ? (
          <ToolActivationSwitch availableTool={item} />
        ) : (
          <AgentSkillSwitch
            item={item}
            agent={agent}
            otherAgentIds={otherAgentIds}
            on={on}
          />
        )
      }
    />
  );
}

/**
 * The switch of a module for one agent among several. Either way it asks the
 * one question that matters: this agent, or every agent?
 */
function AgentSkillSwitch({
  item,
  agent,
  otherAgentIds,
  on,
}: {
  item: AvailableTool;
  agent: Agent;
  otherAgentIds: string[];
  on: boolean;
}) {
  const { t } = useTranslation("agents");
  const catalog = useToolCatalog();
  const { deactivate, isPending: isActivating } = useToolActivation();
  const createClientTool = useCreateClientTool();
  const updateClientTool = useUpdateClientTool();
  const updateAgent = useUpdateAgentConfiguration();
  const deleteAgent = useDeleteAgentConfiguration();
  const [asking, setAsking] = useState<"on" | "off" | null>(null);
  // What was asked for, shown until the refetched list agrees with it.
  const [requested, setRequested] = useState<boolean | null>(null);
  const checked = requested ?? on;

  useEffect(() => {
    if (requested === on) setRequested(null);
  }, [on, requested]);

  const label = catalog.label(item.tool);
  const clientTool = item.clientTool;
  const isPending =
    isActivating ||
    createClientTool.isPending ||
    updateClientTool.isPending ||
    updateAgent.isPending ||
    deleteAgent.isPending;

  const run = async (next: boolean, action: () => Promise<unknown>) => {
    setAsking(null);
    setRequested(next);
    try {
      const ok = await action();
      if (ok === false) setRequested(null);
    } catch (error) {
      setRequested(null);
      toast.error(
        error instanceof Error
          ? error.message
          : t("agents.skills.messages.error"),
      );
    }
  };

  /** This agent back on the company's switch, keeping its own settings. */
  const followCompany = async () => {
    if (!clientTool) return;
    if (overriddenFields(item, agent.id) > 0) {
      await updateAgent.mutateAsync({
        id: clientTool.id,
        agentId: agent.id,
        agentConfig: { enabled: true },
      });
    } else {
      await deleteAgent.mutateAsync({ id: clientTool.id, agentId: agent.id });
    }
  };

  /**
   * The module on for the company, and per agent either on for everyone or
   * off for every agent but this one. The company's row is always the one
   * that says « on »: screens that ask without an agent in mind (the modules
   * page, the FAQ) must see it on too.
   */
  const switchOn = async (only: boolean) => {
    const perAgents: Record<
      string,
      { enabled?: boolean; configuration?: Record<string, unknown> }
    > = {};
    for (const id of [agent.id, ...otherAgentIds]) {
      const kept = _.omit(clientTool?.per_agents?.[id] || {}, "enabled");
      const entry =
        only && id !== agent.id ? { ...kept, enabled: false } : kept;
      if (Object.keys(entry).length > 0) perAgents[id] = entry;
    }
    if (clientTool) {
      await updateClientTool.mutateAsync({
        id: clientTool.id,
        data: { enabled: true, per_agents: perAgents },
      });
    } else {
      await createClientTool.mutateAsync({
        tool_id: item.tool.id,
        enabled: true,
        configuration: toolConfiguration(item.tool),
        per_agents: perAgents,
      });
    }
    toast.success(
      only
        ? t("agents.skills.messages.onForAgent", { label, name: agent.name })
        : t("agents.skills.messages.onForAll", { label }),
    );
  };

  const onlyThisAgent = () => run(true, () => switchOn(true));
  const everyAgent = () => run(true, () => switchOn(false));

  const offThisAgent = () =>
    run(false, async () => {
      if (!clientTool) return;
      await updateAgent.mutateAsync({
        id: clientTool.id,
        agentId: agent.id,
        agentConfig: { enabled: false },
      });
      toast.success(
        t("agents.skills.messages.offForAgent", { label, name: agent.name }),
      );
    });

  const offEveryAgent = () => run(false, () => deactivate(item));

  // When the company has it on and this agent simply follows, switching it
  // back on needs no question: it is the company's setting again. Nor does a
  // sole agent (here only for an override it still holds): « this one or all
  // of them » is the same answer, given as the company's switch would give it.
  const onCheckedChange = (next: boolean) => {
    if (next && clientTool?.enabled) void run(true, followCompany);
    else if (otherAgentIds.length === 0)
      void (next ? everyAgent() : offEveryAgent());
    else setAsking(next ? "on" : "off");
  };

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Switch
        checked={checked}
        disabled={isPending}
        onCheckedChange={onCheckedChange}
        aria-label={t("agents.skills.toggle", { label, name: agent.name })}
      />
      <Dialog
        open={asking !== null}
        onOpenChange={(open) => !open && setAsking(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t(
                asking === "on"
                  ? "agents.skills.askOn.title"
                  : "agents.skills.askOff.title",
                { label },
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                asking === "on"
                  ? "agents.skills.askOn.description"
                  : "agents.skills.askOff.description",
                { label, name: agent.name },
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="ghost" onClick={() => setAsking(null)}>
              {t("common.cancel")}
            </Button>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button
                variant="outline"
                onClick={asking === "on" ? everyAgent : offEveryAgent}
              >
                {t(
                  asking === "on"
                    ? "agents.skills.askOn.everyAgent"
                    : "agents.skills.askOff.everyAgent",
                )}
              </Button>
              <Button onClick={asking === "on" ? onlyThisAgent : offThisAgent}>
                {t("agents.skills.onlyAgent", { name: agent.name })}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
